import { useState } from "react";
import { Avatar, CircularProgress, Divider, Menu, MenuItem } from "@mui/material";
import type { AvatarProps } from "@mui/material";

import { signOut } from "aws-amplify/auth";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDialogs } from "@toolpad/core/useDialogs";

import { RootState } from "@store/store";
import { OptionApplicationMenu } from "@interfaces/slices";
import { setOptionAppSelected } from "@store/slices/componentsSlice";
import { useLazyGetCurrentUserInfoV2Query } from "@services/api/users";
import { CambiarVistaCompania } from "@components/DialogosSegmentos/CambiarVistaCompania";
import { Notificacion } from "@components/DialogosSegmentos/Notificacion";

export interface TopbarProfileProps extends AvatarProps {
    initials: string;
    /** Habilita el menú de cuenta (Administración / Cambiar Vista Empresa / Cerrar sesión). Úsalo sólo cuando el avatar representa al usuario en sesión, no a un tercero. */
    withAccountMenu?: boolean;
}

// `MuiAvatar` is already themed globally (navy-tinted initials chip), so this
// stays a thin wrapper that just defaults the children to `initials`.
export const TopbarProfile = ({ initials, children, src, withAccountMenu, ...rest }: TopbarProfileProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const dialogs = useDialogs();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [reloadingSession, setReloadingSession] = useState(false);

    const sessionUser = useSelector((state: RootState) => state.session.user);
    const permissions = useSelector((state: RootState) => state.session.permissions);
    const companiesUser = useSelector((state: RootState) => state.session.companies);
    const financialPyMIOUser = useSelector((state: RootState) => state.session.financial_inc);

    const [triggerGetCurrentUserInfo] = useLazyGetCurrentUserInfoV2Query();

    const handleClose = () => setAnchorEl(null);

    const handleAdmin = () => {
        dispatch(setOptionAppSelected(OptionApplicationMenu.USUARIOS));
        navigate("/admin");
        handleClose();
    };

    const handleCompanyViewChange = async () => {
        handleClose();
        await dialogs.open(CambiarVistaCompania, {
            fullWidth: true,
            maxWidth: "md",
        });
    };

    const handleReloadSession = async () => {
        try {
            setReloadingSession(true);
            await triggerGetCurrentUserInfo(sessionUser.sso_id).unwrap();
        } catch {
            await dialogs.open(Notificacion, {
                type: "WARNING",
                text: "No se pudo recargar la información de la sesión.",
            });
        }
        setReloadingSession(false);
        handleClose();
    };

    const handleLogout = async () => {
        handleClose();
        dispatch({ type: "CLEAN_STORE" });
        await signOut();
        navigate("/");
    };

    const avatar = (
        <Avatar
            src={src}
            {...rest}
            {...(withAccountMenu
                ? {
                      onClick: (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget),
                      sx: { cursor: "pointer", ...rest.sx },
                      "aria-haspopup": "true",
                      "aria-controls": anchorEl ? "topbar-profile-menu" : undefined,
                  }
                : {})}
        >
            {src ? undefined : (children ?? initials)}
        </Avatar>
    );

    if (!withAccountMenu) return avatar;

    return (
        <>
            {avatar}
            <Menu id="topbar-profile-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {permissions.find((p) => p.module.code === "MADMIN") !== undefined && (
                    <MenuItem onClick={handleAdmin}>Administración</MenuItem>
                )}
                {(companiesUser.length > 1 || financialPyMIOUser.length > 1) && (
                    <MenuItem onClick={handleCompanyViewChange}>Cambiar Vista Empresa</MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleLogout}>Cerrar sesión</MenuItem>
                <MenuItem onClick={handleReloadSession} disabled={reloadingSession}>
                    Recargar sesión
                    {reloadingSession && <CircularProgress size={16} sx={{ ml: 1 }} />}
                </MenuItem>
            </Menu>
        </>
    );
};
